import React, { useState, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaInstagram, FaYoutube, FaTiktok, FaHome, FaArrowLeft } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import RegisterInfluencer from '../Influencer/RegisterInfluencer';
import InfluencerProfile from '../Influencer/InfluencerProfile';
import HireMeForm from '../Influencer/HireMeForm';
import { API_ENDPOINTS, getImageUrl } from '../../utils/GlobalAPI';

const Into = forwardRef((props, ref) => {
  const [index, setIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showHireForm, setShowHireForm] = useState(false);
  const [selectedInfluencerId, setSelectedInfluencerId] = useState(null);
  const [backendInfluencers, setBackendInfluencers] = useState([]);
  const [loadingInfluencers, setLoadingInfluencers] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [sliderKey, setSliderKey] = useState(0);
  const [visibleCount, setVisibleCount] = useState(12);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    openRegisterModal: () => {
      setShowModal(true);
    },
    scrollToFeatured: () => {
      const featuredSection = document.querySelector('[data-section="featured"]');
      if (featuredSection) {
        featuredSection.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  }));

  // Force re-render on resize to fix responsive bugs
  useEffect(() => {
    const handleResize = () => setSliderKey(prev => prev + 1);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 600,
    autoplay: true,
    autoplaySpeed: 4000,
    arrows: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    pauseOnHover: true,
    swipe: true,
    draggable: true,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  // Enhanced image handling function
  const getInfluencerImage = (influencer) => {
    if (influencer.influencerImage) {
      if (influencer.influencerImage.startsWith('http')) {
        return influencer.influencerImage;
      }
      return getImageUrl(influencer.influencerImage);
    }
    
    return 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png';
  };

  // Enhanced field helper with proper fallbacks and formatting
  const getInfluencerData = (influencer, field) => {
    const fieldMappings = {
      name: influencer.influencerName || influencer.name || 'Unknown Influencer',
      bio: influencer.bio || 'No bio available',
      influencerImage: getInfluencerImage(influencer),
      instaSubs: influencer.instaSubs || 'N/A',
      revenue: influencer.revenue ? `₹${influencer.revenue}k` : 'N/A',
      engagement: influencer.engagementPercentage ? `${influencer.engagementPercentage}%` : 'N/A',
      avgViews: influencer.averageViews ? `${influencer.averageViews}k` : 'N/A',
      platform: influencer.platform || 'Instagram', // Default to Instagram
      clients: Array.isArray(influencer.clients) 
        ? influencer.clients 
        : (influencer.brandsWorkedWith ? influencer.brandsWorkedWith.split(',') : []),
      price: influencer.price ? `₹${influencer.price}/hire` : 'Contact for pricing',
      followers: influencer.instaSubs || 'N/A',
      category: influencer.category || 'Other',
      awards: Array.isArray(influencer.awards) ? influencer.awards : []
    };
    
    return fieldMappings[field];
  };

  // Platform icon component
  const PlatformIcon = ({ platform }) => {
    const platformIcons = {
      instagram: <FaInstagram className="inline ml-1 text-pink-600" />,
      youtube: <FaYoutube className="inline ml-1 text-red-600" />,
      tiktok: <FaTiktok className="inline ml-1 text-black" />
    };
    
    const normalizedPlatform = platform?.toLowerCase() || 'instagram';
    return platformIcons[normalizedPlatform] || platformIcons.instagram;
  };

  // Memoized premium & normal influencers
  const filteredInfluencers = useMemo(() => {
    return backendInfluencers.filter(influencer => {
      const matchesCategory = selectedCategory === 'All' || getInfluencerData(influencer, 'category') === selectedCategory;
      const nameVal = (getInfluencerData(influencer, 'name') || '').toLowerCase();
      const bioVal = (getInfluencerData(influencer, 'bio') || '').toLowerCase();
      const matchesSearch =
        nameVal.includes(searchTerm.toLowerCase()) ||
        bioVal.includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [backendInfluencers, selectedCategory, searchTerm]);

  const [featuredInfluencers, normalInfluencers] = useMemo(() => {
    const featured = filteredInfluencers.filter(
      (influencer) => influencer.influencerType === 'Featured Influencer'
    );
    const normal = filteredInfluencers.filter(
      (influencer) => influencer.influencerType !== 'Featured Influencer'
    );
    return [featured, normal];
  }, [filteredInfluencers]);

  // Reset pagination when filters/search change
  useEffect(() => {
    setVisibleCount(12);
  }, [selectedCategory, searchTerm]);

  // Navigation handlers
  const next = useCallback(() => {
    setIndex((i) => (i + 1) % featuredInfluencers.length);
  }, [featuredInfluencers.length]);
  
  const prev = useCallback(() => {
    setIndex((i) => (i - 1 + featuredInfluencers.length) % featuredInfluencers.length);
  }, [featuredInfluencers.length]);

  useEffect(() => {
    if (showModal || showProfileModal || featuredInfluencers.length === 0) return;
    const timer = setInterval(next, 3000);
    return () => clearInterval(timer);
  }, [showModal, showProfileModal, featuredInfluencers.length, next]);

  useEffect(() => {
    document.body.style.overflow = showModal || showProfileModal ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [showModal, showProfileModal]);

  useEffect(() => {
    const fetchInfluencers = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.INFLUENCER);
        const data = await response.json();
        setBackendInfluencers(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching influencers:', error);
      } finally {
        setLoadingInfluencers(false);
      }
    };
    fetchInfluencers();
  }, []);

  const handleHireClick = useCallback((influencerId) => {
    setSelectedInfluencerId(influencerId);
    setShowHireForm(true);
  }, []);

  return (
    <div className="relative">
      {/* Main Content */}

      {/* --- Featured Influencers Carousel --- */}
      <div className="w-full px-4 sm:px-6 py-6 sm:py-8 md:py-12 bg-gradient-to-br from-gray-50 to-blue-50" data-section="featured">
        <div className="max-w-7xl mx-auto text-center mb-6 sm:mb-8 md:mb-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-blue-900 bg-clip-text text-transparent mb-3 sm:mb-4">
              Featured Digital Creators
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed px-4">
              Discover top talent with proven engagement and industry-leading partnerships.
            </p>
          </motion.div>
        </div>
        
        {loadingInfluencers ? (
          <div className="text-center py-8 sm:py-10">
            <div className="inline-block animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-t-2 border-b-2 border-cyan-600"></div>
            <p className="mt-2 sm:mt-3 text-cyan-700 font-medium text-sm sm:text-base">Loading influencers...</p>
          </div>
        ) : featuredInfluencers.length === 0 ? (
          <div className="text-center py-8 sm:py-10 bg-white rounded-xl max-w-2xl mx-auto shadow-sm">
            <p className="text-sm sm:text-base text-gray-600 px-4">No featured influencers found matching your criteria.</p>
            <button 
              onClick={() => {
                setSelectedCategory('All');
                setSearchTerm('');
              }}
              className="mt-3 sm:mt-4 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white rounded-lg sm:rounded-xl font-medium sm:font-semibold text-sm sm:text-base transition-all duration-300 shadow-md hover:shadow-lg"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <section className="w-full pb-8">
            <div className="max-w-5xl mx-auto">
              <Slider key={sliderKey} {...sliderSettings}>
                {featuredInfluencers.map((p, idx) => (
                  <div key={p._id || idx}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: idx * 0.1 }}
                      viewport={{ once: true }}
                      className="bg-white shadow-lg rounded-xl p-4 md:p-5 flex flex-col lg:flex-row items-center gap-4 md:gap-5 transition-all duration-300 min-h-[280px] md:min-h-[320px] mx-1 hover:shadow-xl hover:-translate-y-1 border border-gray-100"
                    >
                      <div className="relative group">
                        <div className="absolute -inset-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
                        <img
                          src={getInfluencerData(p, 'influencerImage')}
                          alt={`${getInfluencerData(p, 'name')} - Featured influencer profile`}
                          className="relative w-24 h-24 md:w-28 md:h-28 rounded-full object-cover border-3 border-white shadow-lg group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                          onError={(e) => {
                            e.target.src = 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png';
                          }}
                        />
                        {getInfluencerData(p, 'awards').length > 0 && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.5 }}
                            className="absolute -bottom-3 -right-3 bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded-full px-3 py-1 text-sm font-bold shadow-lg"
                          >
                            {getInfluencerData(p, 'awards').length} Award{getInfluencerData(p, 'awards').length !== 1 ? 's' : ''}
                          </motion.div>
                        )}
                      </div>
                      <div className="text-left w-full">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="text-lg md:text-xl font-bold bg-gradient-to-r from-gray-900 to-blue-900 bg-clip-text text-transparent">
                            {getInfluencerData(p, 'name')}
                          </h3>
                          <span className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-2 py-1 rounded-full text-xs font-medium shadow-md">
                            {getInfluencerData(p, 'category')}
                          </span>
                        </div>
                        <p className="text-gray-600 text-xs md:text-sm leading-relaxed mb-3 line-clamp-2">
                          {getInfluencerData(p, 'bio')}
                        </p>
                        
                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 md:gap-2 mb-3">
                          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-2 rounded-md border border-blue-200">
                            <div className="text-blue-600 text-xs font-medium mb-0.5">Followers</div>
                            <div className="text-sm font-bold text-gray-900">{getInfluencerData(p, 'instaSubs')}</div>
                          </div>
                          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-2 rounded-md border border-purple-200">
                            <div className="text-purple-600 text-xs font-medium mb-0.5">Engagement</div>
                            <div className="text-sm font-bold text-gray-900">{getInfluencerData(p, 'engagement')}</div>
                          </div>
                          <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-2 rounded-md border border-orange-200">
                            <div className="text-orange-600 text-xs font-medium mb-0.5">Views</div>
                            <div className="text-sm font-bold text-gray-900">{getInfluencerData(p, 'avgViews')}</div>
                          </div>
                          <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-2 rounded-md border border-pink-200">
                            <div className="text-pink-600 text-xs font-medium mb-0.5">Platform</div>
                            <div className="text-sm font-bold text-gray-900 flex items-center">
                              {getInfluencerData(p, 'platform')}
                              <PlatformIcon platform={getInfluencerData(p, 'platform')} />
                            </div>
                          </div>
                          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-2 rounded-md border border-indigo-200">
                            <div className="text-indigo-600 text-xs font-medium mb-0.5">Price</div>
                            <div className="text-sm font-bold text-gray-900">{getInfluencerData(p, 'price')}</div>
                          </div>
                        </div>
                        
                        {/* Clients */}
                        {getInfluencerData(p, 'clients').length > 0 && (
                          <div className="mb-3">
                            <p className="font-medium text-gray-700 mb-1 text-xs">Brands Worked With:</p>
                            <div className="flex flex-wrap gap-1">
                              {getInfluencerData(p, 'clients').slice(0, 2).map((client, i) => (
                                <span
                                  key={client + '_' + i}
                                  className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-2 py-0.5 text-xs rounded-full font-medium shadow-sm"
                                >
                                  {client.trim()}
                                </span>
                              ))}
                              {getInfluencerData(p, 'clients').length > 2 && (
                                <span className="bg-gray-200 text-gray-700 px-2 py-0.5 text-xs rounded-full font-medium">
                                  +{getInfluencerData(p, 'clients').length - 2} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Hire Me Button */}
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleHireClick(p._id)}
                          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs md:text-sm font-semibold py-2 rounded-md shadow-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 hover:shadow-xl"
                        >
                          Hire {getInfluencerData(p, 'name').split(' ')[0]} →
                        </motion.button>
                      </div>
                    </motion.div>
                  </div>
                ))}
              </Slider>
            </div>
          </section>
        )}
      </div>

      {/* Search and Filter Section */}
      <section className="py-8 sm:py-12 md:py-16 bg-gradient-to-r from-gray-900 to-blue-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white/10 backdrop-blur-lg p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl shadow-2xl border border-white/20"
          >
            <div className="text-center mb-6 sm:mb-8">
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2 sm:mb-4">Find Your Perfect Match</h3>
              <p className="text-white/80 text-sm sm:text-base md:text-lg">Search and filter through our curated collection of top digital creators</p>
            </div>
            
            <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 items-center justify-between">
              <div className="w-full lg:w-2/3">
              <div className="relative">
                <input
                  type="text"
                    placeholder="Search by name, bio, or category..."
                    className="w-full px-4 sm:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl border-2 border-white/20 bg-white/10 text-white placeholder-white/60 focus:outline-none focus:ring-4 focus:ring-blue-500/50 focus:border-blue-400 pl-10 sm:pl-14 text-sm sm:text-base md:text-lg backdrop-blur-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <svg 
                    className="absolute left-3 sm:left-5 top-3 sm:top-5 h-5 w-5 sm:h-6 sm:w-6 text-white/60" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
              <div className="w-full lg:w-1/3 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <select
                  className="flex-1 px-4 sm:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl border-2 border-white/20 bg-white/10 text-white focus:outline-none focus:ring-4 focus:ring-blue-500/50 focus:border-blue-400 appearance-none text-sm sm:text-base md:text-lg backdrop-blur-sm"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                  <option value="All" className="bg-gray-800 text-white">All Categories</option>
                  <option value="Food" className="bg-gray-800 text-white">Food & Beverage</option>
                  <option value="Travel" className="bg-gray-800 text-white">Travel</option>
                  <option value="Finance" className="bg-gray-800 text-white">Finance</option>
                  <option value="Fitness" className="bg-gray-800 text-white">Fitness</option>
                  <option value="Fashion" className="bg-gray-800 text-white">Fashion</option>
                  <option value="Gaming" className="bg-gray-800 text-white">Gaming</option>
                  <option value="Tech" className="bg-gray-800 text-white">Technology</option>
                  <option value="Education" className="bg-gray-800 text-white">Education</option>
                  <option value="Entertainment" className="bg-gray-800 text-white">Entertainment</option>
                  <option value="Other" className="bg-gray-800 text-white">Other</option>
              </select>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('All');
                }}
                  className="px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white rounded-xl sm:rounded-2xl transition-all duration-300 whitespace-nowrap font-medium sm:font-semibold text-sm sm:text-base shadow-lg hover:shadow-xl"
              >
                Clear Filters
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* --- All Influencers Grid --- */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 sm:mb-12 gap-4"
          >
            <div className="w-full sm:w-auto">
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-blue-900 bg-clip-text text-transparent mb-3 sm:mb-4">
                {selectedCategory === 'All' ? 'All Digital Creators' : `${selectedCategory} Creators`}
              </h2>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                <span className="bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs sm:text-sm md:text-base font-semibold px-2 sm:px-3 py-1 sm:py-1 md:px-4 md:py-2 rounded-full shadow-lg">
                {filteredInfluencers.length} found
              </span>
                <div className="text-gray-600 text-xs sm:text-sm md:text-base">
                  Showing {filteredInfluencers.length} of {backendInfluencers.length} creators
                </div>
              </div>
            </div>
          </motion.div>

          {loadingInfluencers ? (
            <div className="text-center py-12 sm:py-16 md:py-20">
              <div className="inline-block animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 border-t-2 border-b-2 border-cyan-600"></div>
              <p className="mt-3 sm:mt-4 text-cyan-700 font-medium text-sm sm:text-base">Loading influencers...</p>
            </div>
          ) : filteredInfluencers.length === 0 ? (
            <div className="text-center py-12 sm:py-16 bg-white rounded-xl shadow-sm">
              <svg 
                className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-2 text-base sm:text-lg font-medium text-gray-900">No creators found</h3>
              <p className="mt-1 text-sm sm:text-base text-gray-500 px-4">Try adjusting your search or filter to find the perfect digital creator for your brand.</p>
              <div className="mt-4 sm:mt-6">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('All');
                  }}
                  className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white rounded-lg sm:rounded-xl font-medium sm:font-semibold text-sm sm:text-base transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  Reset filters
                </button>
              </div>
            </div>
          ) : (
            <>
              <style>{`@keyframes gradientMove{0%{background-position:0% 50%}100%{background-position:200% 50%}}`}</style>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4 max-w-6xl mx-auto">
              {filteredInfluencers.slice(0, visibleCount).map((p, idx) => {
                const isFeatured = p.influencerType === 'Featured Influencer';
                return (
                <motion.div
                  key={p._id || idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: (idx % 12) * 0.05 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -4, scale: 1.01 }}
                >
                  <div
                    className={isFeatured ? 'rounded-lg p-[2px] overflow-hidden' : ''}
                    style={isFeatured ? { 
                      background: 'linear-gradient(90deg,#60a5fa,#a78bfa,#f472b6,#60a5fa)',
                      backgroundSize: '200% 200%',
                      animation: 'gradientMove 3s linear infinite',
                      boxShadow: '0 0 18px rgba(99,102,241,0.25)'
                    } : undefined}
                  >
                  <div className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 border ${isFeatured ? 'border-transparent' : 'border-gray-100'} group`}>
                    <div className="p-4">
                    <div className="flex items-start mb-3">
                      <div className="relative group/avatar">
                      <img
                        src={getInfluencerData(p, 'influencerImage')}
                        alt={`${getInfluencerData(p, 'name')} - Digital creator profile`}
                          className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-sm group-hover/avatar:scale-105 transition-transform duration-300"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png';
                        }}
                      />
                        <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full opacity-0 group-hover/avatar:opacity-15 transition-opacity duration-300"></div>
                      </div>
                      <div className="ml-2 flex-1">
                        <div className="flex justify-between items-start">
                          <h3 className="text-sm font-bold bg-gradient-to-r from-gray-900 to-blue-900 bg-clip-text text-transparent">
                            {getInfluencerData(p, 'name')}
                          </h3>
                          {isFeatured ? (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-white text-transparent bg-clip-text" style={{backgroundImage:'linear-gradient(90deg,#60a5fa,#a78bfa,#f472b6,#60a5fa)', backgroundSize:'200% 200%', animation:'gradientMove 3s linear infinite'}}>FEATURED</span>
                          ) : getInfluencerData(p, 'awards').length > 0 && (
                            <span className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                              {getInfluencerData(p, 'awards').length}A
                            </span>
                          )}
                        </div>
                        <p className="text-gray-600 mt-1 text-xs line-clamp-2 leading-relaxed">
                          {getInfluencerData(p, 'bio')}
                        </p>
                        <div className="mt-1">
                          <span className="inline-flex items-center bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs px-2 py-0.5 rounded-full font-medium shadow-sm">
                            {getInfluencerData(p, 'category')}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-2 rounded-md border border-blue-200">
                        <div className="text-blue-600 text-xs font-medium mb-0.5">Followers</div>
                        <div className="text-sm font-bold text-gray-900">{getInfluencerData(p, 'instaSubs') || 'N/A'}</div>
                      </div>
                      <div className="bg-gradient-to-br from-green-50 to-green-100 p-2 rounded-md border border-green-200">
                        <div className="text-green-600 text-xs font-medium mb-0.5">Engagement</div>
                        <div className="text-sm font-bold text-gray-900">{getInfluencerData(p, 'engagement') || 'N/A'}</div>
                      </div>
                      <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-2 rounded-md border border-purple-200">
                        <div className="text-purple-600 text-xs font-medium mb-0.5">Views</div>
                        <div className="text-sm font-bold text-gray-900">{getInfluencerData(p, 'avgViews') || 'N/A'}</div>
                      </div>
                      <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-2 rounded-md border border-orange-200">
                        <div className="text-orange-600 text-xs font-medium mb-0.5">Platform</div>
                        <div className="text-sm font-bold text-gray-900 flex items-center">
                          {getInfluencerData(p, 'platform') || 'N/A'}
                          {getInfluencerData(p, 'platform') && <PlatformIcon platform={getInfluencerData(p, 'platform')} />}
                        </div>
                      </div>
                    </div>

                    {/* Worked With Section - Always present for symmetry */}
                    <div className="mb-3">
                      <div className="text-xs font-medium text-gray-600 mb-1">Worked With</div>
                        <div className="flex flex-wrap gap-1">
                        {getInfluencerData(p, 'clients').length > 0 ? (
                          <>
                            {getInfluencerData(p, 'clients').slice(0, 2).map((client, i) => (
                            <span
                              key={client + '_' + i}
                                className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-1.5 py-0.5 text-xs rounded-full font-medium shadow-sm"
                            >
                              {client.trim()}
                            </span>
                          ))}
                            {getInfluencerData(p, 'clients').length > 2 && (
                              <span className="bg-gray-200 text-gray-700 px-1.5 py-0.5 text-xs rounded-full font-medium">
                                +{getInfluencerData(p, 'clients').length - 2}
                            </span>
                          )}
                          </>
                        ) : (
                          <span className="text-gray-400 text-xs italic">No brands yet</span>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-between items-center">
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-0.5">Price</div>
                        <div className="text-sm font-bold text-gray-900">{getInfluencerData(p, 'price') || 'Contact for pricing'}</div>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleHireClick(p._id)}
                        className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-xs font-semibold rounded-lg transition-all duration-300 shadow-md hover:shadow-lg"
                      >
                        Hire Now
                      </motion.button>
                    </div>
                  </div>
                  </div>
                  </div>
                </motion.div>
              );})}
            </div>
            {visibleCount < filteredInfluencers.length && (
              <div className="flex justify-center mt-6">
                <button
                  onClick={() => setVisibleCount(v => v + 12)}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all"
                >
                  See more
                </button>
              </div>
            )}
            </>
          )}
        </div>
      </section>

      {/* Back to Home Button */}
      <div className="fixed z-50 bottom-8 left-8">
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Link
            to="/"
            className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-2xl shadow-2xl transition-all duration-300 group relative backdrop-blur-sm"
            aria-label="Back to Home"
          >
            <FaHome className="w-7 h-7" />
            <span className="absolute opacity-0 group-hover:opacity-100 bg-gray-900 text-white text-sm rounded-lg py-2 px-3 -top-12 left-1/2 transform -translate-x-1/2 whitespace-nowrap transition-all duration-300 shadow-lg">
              Back to Home
            </span>
          </Link>
        </motion.div>
      </div>

      {/* Floating Action Buttons - Fixed */}
      <div className="fixed z-50 bottom-8 right-8 space-y-4">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={(e) => {
            e.preventDefault();
            setShowProfileModal(true);
          }}
          className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-2xl shadow-2xl transition-all duration-300 group relative backdrop-blur-sm"
          aria-label="Open Profile"
        >
          <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className="absolute opacity-0 group-hover:opacity-100 bg-gray-900 text-white text-sm rounded-lg py-2 px-3 -top-12 left-1/2 transform -translate-x-1/2 whitespace-nowrap transition-all duration-300 shadow-lg">
            View Profile
          </span>
        </motion.button>
        
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={(e) => {
            e.preventDefault();
            setShowModal(true);
          }}
          className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-2xl shadow-2xl transition-all duration-300 group relative backdrop-blur-sm"
          aria-label="Join Now"
        >
          <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
            <span className="absolute opacity-0 group-hover:opacity-100 bg-gray-900 text-white text-sm rounded-lg py-2 px-3 -top-12 left-1/2 transform -translate-x-1/2 whitespace-nowrap transition-all duration-300 shadow-lg">
              Join Now
          </span>
        </motion.button>
      </div>

      {/* Modals - Professionally Fixed */}
     {typeof window !== 'undefined' && createPortal(
  <AnimatePresence>
    {showProfileModal && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[101] flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm"
        onClick={(e) => {
          // Close modal when clicking on backdrop
          if (e.target === e.currentTarget) {
            setShowProfileModal(false);
          }
        }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="w-full max-w-7xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]"
          onClick={(e) => {
            // Prevent modal from closing when clicking inside
            e.stopPropagation();
          }}
        >
          {/* Single Header - Removed duplicate title */}
          <div className="bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-600 px-8 py-5 flex justify-between items-center border-b border-blue-400/20">
            <h2 className="text-2xl font-bold text-white tracking-tight">Influencer Profile</h2>
            <motion.button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowProfileModal(false);
              }}
              className="p-2.5 rounded-full bg-white/20 text-white hover:bg-white/30 transition-all backdrop-blur-sm"
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </motion.button>
          </div>
          <div className="overflow-y-auto p-8">
            <InfluencerProfile 
              onClose={() => setShowProfileModal(false)}
              isAuthenticated={true}
              isModal={true}
            />
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>,
  document.body
)}

      {typeof window !== 'undefined' && createPortal(
        <AnimatePresence>
          {showModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm"
              onClick={(e) => {
                // Close modal when clicking on backdrop
                if (e.target === e.currentTarget) {
                  setShowModal(false);
                }
              }}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                className="w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
                onClick={(e) => {
                  // Prevent modal from closing when clicking inside
                  e.stopPropagation();
                }}
              >
                <div className="bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-4 flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-white">Join Our Platform</h2>
                  <motion.button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowModal(false);
                    }}
                    className="p-2 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </motion.button>
                </div>
                <div className="overflow-y-auto p-6">
                  <RegisterInfluencer onClose={() => setShowModal(false)} />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {typeof window !== 'undefined' && createPortal(
        <AnimatePresence>
          {showHireForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[102] flex items-start justify-center p-2 sm:p-4 bg-black/30 backdrop-blur-sm overflow-y-auto py-8"
              onClick={(e) => {
                // Close modal when clicking on backdrop
                if (e.target === e.currentTarget) {
                  setShowHireForm(false);
                  setSelectedInfluencerId(null);
                }
              }}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                className="w-full max-w-xs sm:max-w-sm md:max-w-2xl lg:max-w-4xl xl:max-w-5xl bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh] sm:max-h-[95vh] lg:max-h-[95vh] xl:max-h-[95vh]"
                onClick={(e) => {
                  // Prevent modal from closing when clicking inside
                  e.stopPropagation();
                }}
              >
                <div className="bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-4 flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-white">Hire Creator</h2>
                  <motion.button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowHireForm(false);
                      setSelectedInfluencerId(null);
                    }}
                    className="p-2 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </motion.button>
                </div>
                <div className="p-0 sm:p-2 lg:p-6 flex-1 overflow-hidden">
                  <HireMeForm
                    influencerId={selectedInfluencerId}
                    influencerData={backendInfluencers.find(inf => inf._id === selectedInfluencerId)}
                    onClose={() => {
                      setShowHireForm(false);
                      setSelectedInfluencerId(null);
                    }}
                  />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
});

export default Into;
